import React, { useState, useEffect } from 'react';

const PRAYER_ORDER = ['shacharit', 'mincha', 'maariv', 'other'];
const PRAYER_LABEL = { shacharit: 'שחרית', mincha: 'מנחה', maariv: 'מעריב' };

const PRAYER_ACCENT = {
  shacharit: '#1e3a5f',
  mincha:    '#b8860b',
  maariv:    '#374151',
  other:     '#64748b',
};

const ZMAN_SHORT = {
  daySunset: 'השקיעה', dayNetz: 'הנץ', dayAlot: 'עלות השחר', dayChatzot: 'חצות',
  dayMinchaGedola: 'מנחה גדולה', dayMinchaKetana: 'מנחה קטנה', dayPlagHamincha: 'פלג המנחה',
  dayTzeit7083: 'צאת הכוכבים', dayTzeit85: 'צאת הכוכבים', dayTzeit42: 'צאת הכוכבים',
  dayTzeit50: 'צאת הכוכבים', dayTzeit72: 'צאת הכוכבים',
  daySofZmanShma: 'סוף זמן שמע', daySofZmanShmaMGA: 'סוף זמן שמע',
  daySofZmanTfilla: 'סוף זמן תפילה', daySofZmanTfillaMGA: 'סוף זמן תפילה',
};
const formatTimeRule = (ct) => {
  if (ct.type === 'fixed') return 'שעה קבועה';
  if (ct.type === 'relative') {
    const label = ZMAN_SHORT[ct.baseTime] || ct.baseTime;
    const offset = parseInt(ct.offset) || 0;
    if (offset === 0) return label;
    const abs = Math.abs(offset);
    return offset < 0 ? `${abs} דק׳ לפני ${label}` : `${abs} דק׳ אחרי ${label}`;
  }
  return null;
};

const DAY_NAMES = { 0: 'א׳', 1: 'ב׳', 2: 'ג׳', 3: 'ד׳', 4: 'ה׳', 5: 'ו׳', roshChodesh: 'ר״ח' };

const todayDow = new Date().getDay();
const todayHebrewDay = (() => {
  try { return parseInt(new Intl.DateTimeFormat('he-u-ca-hebrew', { day: 'numeric' }).format(new Date())); }
  catch { return 0; }
})();
const isRoshChodeshToday = todayHebrewDay === 1 || todayHebrewDay === 30;
const isOverrideActive = (days) =>
  (days || []).includes(todayDow) || (isRoshChodeshToday && (days || []).includes('roshChodesh'));

const toMinutes = (t) => {
  if (!t || t === '--:--') return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const StatusBox = ({ msg, isError }) => (
  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: 'rtl', fontSize: '1.125rem', color: isError ? '#c0392b' : '#94a3b8' }}>
    {msg}
  </div>
);

/**
 * Timeline — ציר זמן אנכי שמציג את התפילות לפי סדר כרונולוגי.
 * קו מרכזי רציף עם נקודות צבעוניות לכל תפילה,
 * מבוקר → ערב, נותן תחושת זרימה של היום.
 */
const Timeline = ({ processedCustomTimes, loading, error, title = 'תפילות יום חול', showTitle = true, recurringDisplay = 'compact', showRuleMode = false, highlightNextMinyan = false }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  if (loading) return <StatusBox msg="טוען..." />;
  if (error) return <StatusBox msg={error} isError />;
  if (!processedCustomTimes?.length) return <StatusBox msg="אין זמני תפילה" />;

  // Build flat list of entries sorted chronologically
  const entries = [];
  const sorted = [...processedCustomTimes].sort((a, b) =>
    PRAYER_ORDER.indexOf(a.prayer || 'other') - PRAYER_ORDER.indexOf(b.prayer || 'other')
  );

  for (const ct of sorted) {
    const label = ct.label || PRAYER_LABEL[ct.prayer] || '';
    const accent = PRAYER_ACCENT[ct.prayer] || PRAYER_ACCENT.other;
    const isRecurring = ct.type === 'recurring';

    if (isRecurring && recurringDisplay === 'full' && ct.resolvedSlots?.length > 0) {
      // In full mode, show each slot as its own entry
      entries.push({
        label,
        accent,
        prayer: ct.prayer,
        isRecurring: true,
        time: `${ct.resolvedSlots[0]} – ${ct.resolvedSlots[ct.resolvedSlots.length - 1]}`,
        timeMin: toMinutes(ct.resolvedSlots[0]),
        interval: ct.interval,
        slots: ct.resolvedSlots,
        overrides: ct.resolvedOverrides || ct.dayOverrides || [],
        rule: null,
        ct,
      });
    } else if (isRecurring) {
      entries.push({
        label,
        accent,
        prayer: ct.prayer,
        isRecurring: true,
        time: ct.resolvedSlots?.length ? `${ct.resolvedSlots[0]} – ${ct.resolvedSlots[ct.resolvedSlots.length - 1]}` : '--:--',
        timeMin: toMinutes(ct.resolvedSlots?.[0]),
        interval: ct.interval,
        slots: null,
        overrides: ct.resolvedOverrides || ct.dayOverrides || [],
        rule: null,
        ct,
      });
    } else {
      entries.push({
        label,
        accent,
        prayer: ct.prayer,
        isRecurring: false,
        time: ct.resolvedTime || '--:--',
        timeMin: toMinutes(ct.resolvedTime),
        interval: null,
        slots: null,
        overrides: ct.resolvedOverrides || ct.dayOverrides || [],
        rule: !showRuleMode ? formatTimeRule(ct) : null,
        showRuleInstead: showRuleMode && ct.type === 'relative' ? formatTimeRule(ct) : null,
        ct,
      });
    }
  }

  // Sort by time
  entries.sort((a, b) => {
    if (a.timeMin < 0 && b.timeMin < 0) return 0;
    if (a.timeMin < 0) return 1;
    if (b.timeMin < 0) return -1;
    return a.timeMin - b.timeMin;
  });

  const nowMin = now.getHours() * 60 + now.getMinutes();
  const nextIdx = highlightNextMinyan
    ? entries.findIndex(e => e.timeMin >= nowMin && e.ct?.isActiveToday !== false)
    : -1;

  return (
    <div style={{
      width: '100%', height: '100%', direction: 'rtl',
      display: 'flex', flexDirection: 'column',
      backgroundColor: '#ffffff', boxSizing: 'border-box', overflow: 'hidden',
    }}>
      {showTitle && (
        <div style={{
          padding: '0.875rem 1.5rem',
          borderBottom: '1px solid #e2e8f0',
          flexShrink: 0,
        }}>
          <span style={{ fontWeight: 700, fontSize: '1.25rem', color: '#1e3a5f' }}>{title}</span>
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1rem 1.25rem 1.5rem', boxSizing: 'border-box' }}>
        {entries.map((entry, idx) => {
          const isLast = idx === entries.length - 1;
          const isNext = highlightNextMinyan && idx === nextIdx;

          return (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'stretch',
              minHeight: '60px',
              backgroundColor: isNext ? '#eff6ff' : 'transparent',
              borderRadius: isNext ? '8px' : 0,
              marginBottom: isNext ? '4px' : 0,
              padding: isNext ? '4px 4px 4px 0' : 0,
              boxShadow: isNext ? '0 2px 8px rgba(30,58,95,0.12)' : 'none',
            }}>
              {/* Timeline rail */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '28px',
                flexShrink: 0,
                marginLeft: '16px',
              }}>
                {/* Dot */}
                <div style={{
                  width: isNext ? '18px' : '14px',
                  height: isNext ? '18px' : '14px',
                  borderRadius: '50%',
                  backgroundColor: entry.accent,
                  border: isNext ? `3px solid ${entry.accent}` : '3px solid #ffffff',
                  boxShadow: isNext ? `0 0 0 3px ${entry.accent}50` : `0 0 0 2px ${entry.accent}40`,
                  flexShrink: 0,
                  marginTop: '4px',
                }} />
                {/* Connecting line */}
                {!isLast && (
                  <div style={{
                    flex: 1,
                    width: '2px',
                    backgroundColor: '#e2e8f0',
                    minHeight: '20px',
                  }} />
                )}
              </div>

              {/* Content */}
              <div style={{
                flex: 1,
                paddingBottom: isLast ? 0 : '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '3px',
              }}>
                {isNext && (
                  <span style={{
                    display: 'inline-block', marginBottom: '4px',
                    fontSize: '10px', fontWeight: 700, color: '#fff',
                    backgroundColor: '#1e3a5f', borderRadius: '10px', padding: '2px 8px',
                  }}>המניין הבא</span>
                )}
                {/* Header row: label + time */}
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: '12px',
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                    <span style={{
                      fontSize: '1.1rem', fontWeight: 700,
                      color: entry.accent,
                    }}>
                      {entry.label}
                    </span>
                    {(entry.rule || entry.ct.daysLabel) && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 4px', alignItems: 'baseline' }}>
                        {entry.rule && (
                          <span style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#64748b', fontWeight: 500, lineHeight: 1 }}>
                            {entry.rule}{entry.ct.daysLabel ? ' ·' : ''}
                          </span>
                        )}
                        {entry.ct.daysLabel && (
                          <span style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#64748b', fontWeight: 500, lineHeight: 1 }}>
                            {entry.ct.daysLabel}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {entry.isRecurring ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <span style={{
                        fontSize: '0.7rem', fontWeight: 600, color: '#166534',
                        backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
                        borderRadius: '99px', padding: '1px 8px',
                      }}>
                        כל {entry.interval} דק׳
                      </span>
                      <span style={{
                        fontSize: '1.1rem', fontWeight: 700, color: '#0f172a',
                        fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                      }}>
                        {entry.time}
                      </span>
                    </div>
                  ) : entry.showRuleInstead ? (
                    <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#475569', textAlign: 'left' }}>
                      {entry.showRuleInstead}
                    </span>
                  ) : (
                    <span style={{
                      fontSize: '1.5rem', fontWeight: 800,
                      color: '#0f172a',
                      fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                      letterSpacing: '-0.03em', lineHeight: 1,
                      flexShrink: 0,
                    }}>
                      {entry.time}
                    </span>
                  )}
                </div>

                {/* Full slots grid */}
                {entry.isRecurring && entry.slots && (
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: '3px',
                    direction: 'ltr', marginTop: '4px',
                  }}>
                    {entry.slots.map((slot, i) => (
                      <span key={i} style={{
                        fontSize: '0.7rem', fontWeight: 600, color: '#475569',
                        fontVariantNumeric: 'tabular-nums',
                        backgroundColor: '#f8fafc', border: '1px solid #e2e8f0',
                        borderRadius: '4px', padding: '1px 5px',
                      }}>
                        {slot}
                      </span>
                    ))}
                  </div>
                )}

                {/* Day overrides */}
                {entry.overrides.map((ov, oi) => {
                  const active = isOverrideActive(ov.days);
                  const dayLabel = (ov.days || []).map(d => DAY_NAMES[d]).filter(Boolean).join(' ');
                  const time = ov.resolvedTime || ov.time || '--:--';
                  return (
                    <div key={oi} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      marginTop: '3px',
                      padding: active ? '3px 8px' : '2px 4px',
                      borderRadius: '6px',
                      backgroundColor: active ? '#f0fdf4' : 'transparent',
                      border: active ? '1px solid #bbf7d0' : 'none',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {active && (
                          <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#16a34a', flexShrink: 0, display: 'inline-block' }} />
                        )}
                        <span style={{ fontSize: '0.75rem', color: active ? '#15803d' : '#94a3b8', fontWeight: active ? 600 : 400 }}>{dayLabel}</span>
                      </div>
                      <span style={{
                        fontSize: '0.85rem', fontWeight: 700, color: active ? '#15803d' : '#94a3b8',
                        fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                      }}>
                        {time}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Timeline;
